import React, { useEffect, useState } from 'react';
import heroBg from '../assets/hero-bg.png';
import heroPhone from '../assets/hero-phone.png';

const PRIMARY = '#3498db';
const NAVY = '#0F172A';
const GREEN = '#10B981';

const Hero: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 940);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth >= 940 && window.innerWidth < 1150);
  const [phoneLoaded, setPhoneLoaded] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    // Detect when the hero background image is ready (may already be cached)
    const img = new window.Image();
    img.onload = () => setBgLoaded(true);
    img.src = heroBg;
    if (img.complete) setBgLoaded(true);
  }, []);
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 940);
      setIsNarrow(w >= 940 && w < 1150);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const FOOTER_GRADIENT = 'linear-gradient(135deg, #0F172A 0%, #1e3a5f 60%, #1a6fa8 100%)';

  /* ─── Shared text block ─────────────────────────────────────────── */
  const textContent = (
    <div style={{ maxWidth: isMobile ? '100%' : '580px' }}>

      {/* Live badge — desktop only (mobile shows it on the image) */}
      {!isMobile && <div className="hero-fadein" style={{
        animationDelay: '0s',
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: 'rgba(10,20,50,0.55)', backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.15)',
        padding: '7px 16px', borderRadius: 50, marginBottom: 'clamp(8px, 2.78vh, 24px)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
      }}>
        <span className="hero-live-dot" style={{
          width: 8, height: 8, borderRadius: '50%', background: GREEN,
          flexShrink: 0, display: 'inline-block', boxShadow: '0 0 0 3px rgba(16,185,129,0.25)',
        }} />
        <span style={{ fontSize: 15, fontWeight: 700, color: '#fff', letterSpacing: '0.01em' }}>Pickup Now Live In Delhi NCR</span>
      </div>}

      {/* Tagline */}
      <div className="hero-fadein" style={{ animationDelay: '0.05s', marginBottom: isMobile ? 18 : 'clamp(6px, 2.43vh, 21px)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            width: 3, minHeight: 52, borderRadius: 4,
            background: `linear-gradient(180deg, ${PRIMARY} 0%, #10B981 100%)`,
            flexShrink: 0, marginTop: 3,
          }} />
          <div>
            <p style={{
              fontSize: isMobile ? '1.2rem' : 'clamp(1.1rem, 2.69vh, 1.45rem)', fontWeight: 800,
              color: '#ffffff', margin: 0, lineHeight: 1.35, letterSpacing: '-0.01em',
            }}>
              You Focus on Business.<br />
              <span style={{ color: PRIMARY }}>We Handle Logistics.</span>
            </p>
            <p style={{
              margin: '8px 0 0 0', fontSize: isMobile ? '0.88rem' : '0.97rem',
              fontWeight: 500, color: 'rgba(255,255,255,0.5)',
              letterSpacing: '0.03em', fontStyle: 'italic',
            }}>
              Crafted with care for India's growing businesses.
            </p>
          </div>
        </div>
      </div>

      {/* Heading */}
      <h1 className="hero-fadein" style={{
        animationDelay: '0.12s',
        fontSize: isMobile ? '2.2rem' : 'clamp(2.1rem, 5.74vh, 3.1rem)', fontWeight: 900,
        color: '#ffffff', lineHeight: 1.06, letterSpacing: '-0.03em', marginBottom: isMobile ? 20 : 'clamp(8px, 2.78vh, 24px)',
      }}>
        India&apos;s Smartest<br />
        <span style={{
          background: `linear-gradient(90deg, ${PRIMARY} 0%, #60c0ff 100%)`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
        }}>B2B Logistics</span><br />
        Partner
      </h1>

      {/* Benefits */}
      <div className="hero-fadein" style={{ animationDelay: '0.22s', display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 'clamp(5px, 1.39vh, 12px)', marginBottom: isMobile ? 28 : 'clamp(12px, 3.7vh, 32px)' }}>
        {['24/7 Dedicated Relationship Manager', 'Up to 30% Faster Inter City Deliveries', '50Kg or 5000Kg, We\'ve Got You Covered'].map((benefit) => (
          <div key={benefit} style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%',
              background: 'rgba(16,185,129,0.15)', border: '1.5px solid rgba(16,185,129,0.7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.88)' }}>{benefit}</span>
          </div>
        ))}
      </div>

      {/* CTA Buttons */}
      <div className="hero-fadein" style={{ animationDelay: '0.32s', display: 'flex', gap: isMobile ? 10 : 14, flexWrap: isMobile ? 'nowrap' : 'wrap', marginBottom: isMobile ? 28 : 'clamp(12px, 4.28vh, 37px)' }}>
        <a href="/#cta" className="hero-btn-p" style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: PRIMARY, color: '#fff', padding: isMobile ? '12px 16px' : '15px 30px', borderRadius: 12,
          fontWeight: 700, fontSize: isMobile ? 13 : 15, textDecoration: 'none',
          boxShadow: '0 4px 20px rgba(52,152,219,0.45)', transition: 'all 0.22s ease',
          flex: isMobile ? '1' : undefined, whiteSpace: 'nowrap',
        }}>
          Start Shipping Today
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M5 12H19M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
        <a href="/tracking" className="hero-btn-s" style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: 'rgba(255,255,255,0.1)', color: '#fff', padding: isMobile ? '12px 16px' : '15px 30px', borderRadius: 12,
          fontWeight: 700, fontSize: isMobile ? 13 : 15, textDecoration: 'none',
          border: '1.5px solid rgba(255,255,255,0.35)', backdropFilter: 'blur(8px)',
          transition: 'all 0.22s ease',
          flex: isMobile ? '1' : undefined, whiteSpace: 'nowrap',
        }}>
          Track Order
        </a>
      </div>

      {/* Stats */}
      <div className="hero-fadein" style={{
        animationDelay: '0.42s',
        display: isMobile ? 'grid' : 'flex',
        gridTemplateColumns: isMobile ? '1fr 1fr' : undefined,
        gap: isMobile ? '20px 0' : 44,
        flexWrap: 'wrap',
        borderTop: '1px solid rgba(255,255,255,0.12)',
        paddingTop: isMobile ? 20 : 'clamp(10px, 3.01vh, 26px)',
      }}>
        {[
          { top: 'Trusted By', num: '500+', label: 'Businesses' },
          { top: 'Shipping To', num: '1000+', label: 'Cities' },
          { top: 'Network Of', num: '600+', label: 'Transporters' },
          { top: 'Pickup In', num: '30 Min', label: 'In Delhi NCR' },
        ].map((s, i) => (
          <div key={s.label} style={{
            borderLeft: isMobile ? (i % 2 === 1 ? '1px solid rgba(255,255,255,0.1)' : 'none') : 'none',
            paddingLeft: isMobile ? (i % 2 === 1 ? 20 : 0) : 0,
            paddingBottom: isMobile && i < 2 ? 20 : 0,
            borderBottom: isMobile && i < 2 ? '1px solid rgba(255,255,255,0.1)' : 'none',
            textAlign: isMobile ? 'center' as const : undefined,
          }}>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.50)', marginBottom: 4, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.07em' }}>{s.top}</div>
            <div style={{ fontSize: isMobile ? 28 : 'clamp(1.4rem, 3.94vh, 2.125rem)', fontWeight: 800, color: '#ffffff', lineHeight: 1 }}>{s.num}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.50)', marginTop: 5, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.07em' }}>{s.label}</div>
          </div>
        ))}
      </div>

    </div>
  );

  /* ─── MOBILE layout ──────────────────────────────────────────────── */
  if (isMobile) {
    return (
      <>
        <style>{`
          @keyframes heroFadeIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
          .hero-fadein { animation:heroFadeIn 0.8s ease-out both; }
          .hero-btn-p:hover { background:#2980b9 !important; transform:translateY(-2px) !important; box-shadow:0 10px 32px rgba(52,152,219,0.55) !important; }
          .hero-btn-s:hover { background:rgba(255,255,255,0.18) !important; transform:translateY(-2px) !important; }
          @keyframes heroPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.15)} }
          .hero-live-dot { animation:heroPulse 2s ease-in-out infinite; }
        `}</style>

        {/* Full-width image with badge overlay */}
        <div style={{ width: '100%', lineHeight: 0, position: 'relative' }}>
          <img
            src={heroPhone}
            alt="Instaship logistics app"
            fetchPriority="high"
            onLoad={() => setPhoneLoaded(true)}
            style={{ width: '100%', height: 'auto', display: 'block', opacity: phoneLoaded ? 1 : 0, transition: 'opacity 0.5s ease' }}
          />
          {/* Live badge — top-left over image */}
          <div className="hero-fadein" style={{
            position: 'absolute', top: 28, left: 16,
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(10,20,50,0.70)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.18)',
            padding: '7px 14px', borderRadius: 50,
            boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
          }}>
            <span className="hero-live-dot" style={{
              width: 8, height: 8, borderRadius: '50%', background: GREEN,
              flexShrink: 0, display: 'inline-block', boxShadow: '0 0 0 3px rgba(16,185,129,0.25)',
            }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '0.01em' }}>Pickup Now Live In Delhi NCR</span>
          </div>
        </div>

        {/* Text block on footer gradient */}
        <div style={{ background: FOOTER_GRADIENT, padding: '28px 24px 56px' }}>
          {textContent}
        </div>
      </>
    );
  }

  /* ─── DESKTOP layout ─────────────────────────────────────────────── */
  return (
    <section style={{
      position: 'relative', overflow: 'hidden',
      minHeight: '100vh', padding: 'clamp(16px, 5.56vh, 48px) 0',
    }}>
      <style>{`
        @keyframes heroFadeIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        .hero-fadein { animation:heroFadeIn 0.8s ease-out both; }
        .hero-btn-p:hover { background:#2980b9 !important; transform:translateY(-2px) !important; box-shadow:0 10px 32px rgba(52,152,219,0.55) !important; }
        .hero-btn-s:hover { background:rgba(255,255,255,0.18) !important; transform:translateY(-2px) !important; }
        @keyframes heroPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.15)} }
        .hero-live-dot { animation:heroPulse 2s ease-in-out infinite; }
      `}</style>

      {/* Striped base */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        background: `repeating-linear-gradient(-52deg,#dce9f8 0px,#dce9f8 0.3px,#e8f2fc 0.3px,#e8f2fc 56px)`,
        WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)',
        maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)',
      }} />

      {/* Background image — original positioning preserved; fades in once loaded */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        backgroundImage: `url(${heroBg})`,
        backgroundSize: isNarrow ? '55% auto' : 'auto 105%',
        backgroundPosition: 'right center',
        backgroundRepeat: 'no-repeat',
        opacity: bgLoaded ? 1 : 0,
        transition: 'opacity 0.6s ease',
      }} />

      {/* Dark gradient overlay */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2,
        background: 'linear-gradient(to right, rgba(8,18,46,0.95) 0%, rgba(8,18,46,0.80) 20%, rgba(8,18,46,0.58) 38%, rgba(8,18,46,0.28) 55%, rgba(8,18,46,0.1) 68%, transparent 82%)',
      }} />

      {/* Subtle blue accent glow */}
      <div style={{
        position: 'absolute', top: '-10%', left: '-5%', zIndex: 3,
        width: '50%', height: '70%',
        background: 'radial-gradient(ellipse at center, rgba(52,152,219,0.12) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 2, paddingLeft: 'max(24px, 7vw)', paddingRight: 'max(24px, 4vw)' }}>
        {textContent}
      </div>
    </section>
  );
};

export default Hero;